import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { UserResponse, InvitationResponse } from '@vagusx/clients';
import { P } from '@vagusx/shared';
import { UserPlus, RefreshCw, Trash2, Clock, Pencil, UserX, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrentUser } from '@/hooks/use-current-user';
import { DrilldownBanner } from '@/components/dashboard/drilldown-banner';
import { useHasPermission } from '@/hooks/use-permissions';
import {
  useUsers,
  useUser,
  useUpdateUser,
  useDeleteUser,
  useInactiveUsers,
  useReactivateUser,
} from '@/hooks/use-users';
import {
  useInvitations,
  useCreateInvitation,
  useResendInvitation,
  useDeleteInvitation,
} from '@/hooks/use-invitations';
import { useRoles, useRoleMap, resolveRoles } from '@/hooks/use-roles';
import { TablePage, SortableHeader } from '@/components/table-page';
import { DataTable } from '@/components/data-table';
import { MultiSelect, type MultiSelectOption } from '@/components/custom/multi-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { DetailRow, DetailSection } from '@/components/custom/detail-section';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function buildUserColumns(opts: {
  canEdit: boolean;
  onEdit: (id: string) => void;
}): ColumnDef<UserResponse>[] {
  const cols: ColumnDef<UserResponse>[] = [
    {
      accessorKey: 'name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <SortableHeader column={column}>Email</SortableHeader>,
    },
    {
      accessorKey: 'roles',
      header: 'Roles',
      cell: ({ row }) => {
        const roles = row.original.roles;
        if (!roles || roles.length === 0) {
          return <span className="text-muted-foreground text-sm">No roles</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {roles.map((r) => (
              <Badge key={r.id} variant={r.isSystem ? 'default' : 'secondary'}>
                {r.name}
              </Badge>
            ))}
          </div>
        );
      },
    },
  ];

  if (opts.canEdit) {
    cols.push({
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-[64px]', cellClassName: 'text-right' },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          // Hidden until the row is hovered; `focus-visible` keeps it reachable
          // by keyboard. Tailwind gates `group-hover` behind `@media (hover:hover)`,
          // so anything with a touchscreen keeps it permanently visible — including
          // a hybrid laptop, whose *primary* input hovers even when the finger the
          // user actually taps with does not. Without that the button would be
          // invisible but still tappable, swallowing the row tap. `any-pointer`
          // (not `hover`) is the test, because it considers every attached input.
          // No `transition-opacity` here: Button's base `transition-all` already
          // covers opacity, and tailwind-merge would drop it for a narrower class.
          className="h-7 w-7 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(any-pointer:coarse)]:opacity-100"
          aria-label={`Edit ${row.original.firstName} ${row.original.lastName}`}
          onClick={(e) => {
            e.stopPropagation();
            opts.onEdit(row.original.id);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ),
    });
  }

  return cols;
}

// Module-level — never changes.
const inactiveUserColumns = buildUserColumns({ canEdit: false, onEdit: () => undefined });

export default function UsersPage(): React.ReactElement {
  const { data: usersData, isLoading: usersLoading, error: usersError } = useUsers();
  const { data: invitationsData, isLoading: invitationsLoading } = useInvitations();
  const { data: inactiveUsersData, isLoading: inactiveLoading } = useInactiveUsers();
  const roleMap = useRoleMap();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedInactiveUserId, setSelectedInactiveUserId] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const canEdit = useHasPermission(P.USERS_EDIT);
  const canActivate = useHasPermission(P.USERS_ACTIVATE);

  const handleEditFromRow = useCallback((id: string) => {
    setSelectedUserId(id);
    setEditingUserId(id);
  }, []);

  const activeUserColumns = useMemo(
    () => buildUserColumns({ canEdit, onEdit: handleEditFromRow }),
    [canEdit, handleEditFromRow]
  );

  // Enrich users with resolved role details from the cached org roles
  const users: UserResponse[] = useMemo(
    () =>
      (usersData?.items ?? []).map((u) => ({
        ...u,
        roles: resolveRoles(u.roleIds, roleMap),
      })),
    [usersData?.items, roleMap]
  );
  const inactiveUsers: UserResponse[] = useMemo(
    () =>
      (inactiveUsersData ?? []).map((u) => ({
        ...u,
        roles: resolveRoles(u.roleIds, roleMap),
      })),
    [inactiveUsersData, roleMap]
  );
  const invitations: InvitationResponse[] = invitationsData?.items ?? [];
  const pendingInvitations = invitations.filter((inv) => !inv.acceptedAt);

  // Defensive lookup — if a refetch removes the row between renders (e.g.
  // another admin reactivates the same user), close the sheet rather than
  // crash on `find(...)!`.
  const selectedInactiveUser = selectedInactiveUserId
    ? (inactiveUsers.find((u) => u.id === selectedInactiveUserId) ?? null)
    : null;

  return (
    <div className="flex h-full flex-col">
      {/* Dashboard drilldown banner — renders only when ?from=dashboard. */}
      <DrilldownBanner />
      <TablePage<UserResponse, unknown>
        title="Users"
        description="Manage staff accounts and permissions"
        total={users.length || undefined}
        isLoading={usersLoading}
        headerAction={
          canEdit ? (
            <InviteUserDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
          ) : undefined
        }
      >
        <Tabs defaultValue="users" className="flex min-h-0 flex-1 flex-col">
          <PageTabsList className="shrink-0">
            <TabsTrigger value="users">Active ({users.length})</TabsTrigger>
            <TabsTrigger value="invitations">Pending ({pendingInvitations.length})</TabsTrigger>
            <TabsTrigger value="inactive">Inactive ({inactiveUsers.length})</TabsTrigger>
          </PageTabsList>

          <TabsContent value="users" className="mt-4 flex min-h-0 flex-1 flex-col">
            {usersError ? (
              <div className="rounded-md bg-destructive/10 p-4 text-destructive">
                Failed to load users. Please try again.
              </div>
            ) : (
              <DataTable
                columns={activeUserColumns}
                data={users}
                isLoading={usersLoading}
                searchColumn="name"
                searchPlaceholder="Search users..."
                onRowClick={(user) => {
                  setEditingUserId(null);
                  setSelectedUserId(user.id);
                }}
                emptyMessage="No users found."
                fillHeight
              />
            )}
          </TabsContent>

          <TabsContent value="invitations" className="mt-4">
            <InvitationsList invitations={pendingInvitations} isLoading={invitationsLoading} />
          </TabsContent>

          <TabsContent value="inactive" className="mt-4 flex min-h-0 flex-1 flex-col">
            <DataTable
              columns={inactiveUserColumns}
              data={inactiveUsers}
              isLoading={inactiveLoading}
              searchColumn="name"
              searchPlaceholder="Search inactive users..."
              onRowClick={(user) => setSelectedInactiveUserId(user.id)}
              emptyMessage="No inactive users."
              fillHeight
            />
          </TabsContent>
        </Tabs>

        {/* Active User Detail / Edit Sheet */}
        <Sheet
          open={!!selectedUserId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedUserId(null);
              setEditingUserId(null);
            }
          }}
        >
          <SheetContent className="w-full max-w-lg sm:max-w-xl">
            {selectedUserId && (
              <UserDetailSheet
                userId={selectedUserId}
                canEdit={canEdit}
                canActivate={canActivate}
                defaultEditMode={editingUserId === selectedUserId}
                onClose={() => {
                  setSelectedUserId(null);
                  setEditingUserId(null);
                }}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Inactive User Detail Sheet */}
        <Sheet
          open={!!selectedInactiveUser}
          onOpenChange={(open) => !open && setSelectedInactiveUserId(null)}
        >
          <SheetContent className="w-full max-w-lg sm:max-w-xl">
            {selectedInactiveUser && (
              <InactiveUserSheet
                user={selectedInactiveUser}
                canActivate={canActivate}
                onClose={() => setSelectedInactiveUserId(null)}
              />
            )}
          </SheetContent>
        </Sheet>
      </TablePage>
    </div>
  );
}

/** Convert org roles into MultiSelectOption[] for the dropdown */
function useRoleOptions(): MultiSelectOption[] {
  const { data: orgRoles } = useRoles();
  return useMemo(
    () =>
      (orgRoles ?? []).map((r) => ({
        value: r.id,
        label: r.name,
        description: r.isSystem ? 'System role' : undefined,
        badgeVariant: r.isSystem ? ('default' as const) : ('secondary' as const),
      })),
    [orgRoles]
  );
}

// ---------------------------------------------------------------------------
// User Detail / Edit Sheet
// ---------------------------------------------------------------------------

function UserDetailSheet({
  userId,
  canEdit,
  canActivate,
  defaultEditMode,
  onClose,
}: {
  userId: string;
  canEdit: boolean;
  canActivate: boolean;
  /**
   * Whether the sheet should open directly into the editor rather than the
   * read-only detail view. Synced via useEffect so re-opening the sheet for
   * a different user with a different mode flips the state correctly.
   */
  defaultEditMode: boolean;
  onClose: () => void;
}): React.ReactElement {
  const { data: rawUser, isLoading } = useUser(userId);
  const { data: currentUser } = useCurrentUser();
  const roleMap = useRoleMap();
  const deleteUser = useDeleteUser();
  const [isEditing, setIsEditing] = useState(defaultEditMode);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  useEffect(() => {
    setIsEditing(defaultEditMode);
  }, [defaultEditMode, userId]);

  // Enrich user with resolved roles
  const user = useMemo(() => {
    if (!rawUser) return null;
    return { ...rawUser, roles: resolveRoles(rawUser.roleIds, roleMap) };
  }, [rawUser, roleMap]);

  if (isLoading || !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (isEditing) {
    return <UserEditor user={user} onClose={() => setIsEditing(false)} />;
  }

  const isSelf = currentUser?.id === user.id;

  const handleDeactivate = async () => {
    await deleteUser.mutateAsync(user.id);
    toast.success(`${user.firstName} ${user.lastName} has been deactivated`);
    setShowDeactivateDialog(false);
    onClose();
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle>
          {user.firstName} {user.lastName}
        </SheetTitle>
        <SheetDescription>User details</SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          <DetailSection title="Account Information">
            <DetailRow label="Email" value={user.email} />
          </DetailSection>

          <DetailSection title="Assigned Roles">
            {user.roles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.roles.map((r) => (
                  <Badge key={r.id} variant={r.isSystem ? 'default' : 'secondary'}>
                    {r.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">No roles assigned</span>
            )}
          </DetailSection>
        </div>
      </SheetBody>

      {((!isSelf && canActivate) || canEdit) && (
        <SheetActions
          start={
            !isSelf &&
            canActivate && (
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowDeactivateDialog(true)}
              >
                <UserX className="mr-2 h-3.5 w-3.5" />
                Deactivate
              </Button>
            )
          }
        >
          <SheetCancel />
          {canEdit && (
            <Button onClick={() => setIsEditing(true)}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </SheetActions>
      )}

      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate user</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate {user.firstName} {user.lastName}? They will no
              longer be able to log in. You can reactivate them later from the Inactive Users tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeactivate}
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending ? 'Deactivating...' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// User Editor
// ---------------------------------------------------------------------------

function UserEditor({
  user,
  onClose,
}: {
  user: UserResponse;
  onClose: () => void;
}): React.ReactElement {
  const updateUser = useUpdateUser();
  const roleOptions = useRoleOptions();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(user.roleIds);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRoleIds.length === 0) {
      toast.error('At least one role is required');
      return;
    }
    await updateUser.mutateAsync({
      id: user.id,
      data: { firstName, lastName, roleIds: selectedRoleIds },
    });
    toast.success('User updated');
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <SheetHeader>
        <SheetTitle>Edit User</SheetTitle>
        <SheetDescription>Update user name and roles for {user.email}</SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={user.email} disabled />
          </div>

          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <MultiSelect
              options={roleOptions}
              selected={selectedRoleIds}
              onChange={setSelectedRoleIds}
              placeholder="Select roles..."
            />
          </div>
        </div>
      </SheetBody>

      <SheetActions>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <SaveButton type="submit" isPending={updateUser.isPending} isEdit />
      </SheetActions>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Invite User Dialog
// ---------------------------------------------------------------------------

function InviteUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const [email, setEmail] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const createInvitation = useCreateInvitation();
  const roleOptions = useRoleOptions();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRoleIds.length === 0) {
      toast.error('At least one role is required');
      return;
    }
    try {
      await createInvitation.mutateAsync({ email, roleIds: selectedRoleIds });
      toast.success('Invitation sent');
      setEmail('');
      setSelectedRoleIds([]);
      onOpenChange(false);
    } catch {
      // Error is handled by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new user to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="invite-email">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>Roles</Label>
              <MultiSelect
                options={roleOptions}
                selected={selectedRoleIds}
                onChange={setSelectedRoleIds}
                placeholder="Select roles..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createInvitation.isPending}>
              {createInvitation.isPending ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Invitations List
// ---------------------------------------------------------------------------

function InvitationsList({
  invitations,
  isLoading,
}: {
  invitations: InvitationResponse[];
  isLoading: boolean;
}): React.ReactElement {
  const resendInvitation = useResendInvitation();
  const deleteInvitation = useDeleteInvitation();
  const { data: orgRoles } = useRoles();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        Loading invitations...
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-3 mb-4">
          <UserPlus className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="font-medium">No pending invitations</h3>
        <p className="text-sm text-muted-foreground mt-1">Invite users to join your organization</p>
      </div>
    );
  }

  // Build a role name lookup from org roles
  const roleMap = new Map<string, string>();
  orgRoles?.forEach((r) => roleMap.set(r.id, r.name));

  return (
    <div className="space-y-3">
      {invitations.map((invitation) => {
        const isExpired = new Date(invitation.expiresAt) < new Date();
        // Both mutation hooks live at the parent component level (one
        // instance shared by every row), so `mutation.isPending` is a
        // global flag — a previous version had every Resend / trash
        // button spin when one row was clicked. `mutation.variables` is
        // the argument of the most recent `mutate()` call, so this picks
        // out only the single row that's actually in flight. Disabled
        // stays global: concurrent calls against the shared instance
        // would clobber `variables` and orphan the older spinner.
        const isResending =
          resendInvitation.isPending && resendInvitation.variables === invitation.id;

        // Resolve role names from roleIds
        const roleNames = invitation.roles
          ? invitation.roles.map((r) => r.name)
          : invitation.roleIds.map((id) => roleMap.get(id) ?? 'Unknown');

        return (
          <div
            key={invitation.id}
            className="flex items-center justify-between rounded-lg border p-4"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{invitation.email}</span>
                {roleNames.map((name, i) => (
                  <Badge key={i} variant="secondary">
                    {name}
                  </Badge>
                ))}
                {isExpired ? (
                  <Badge variant="destructive" className="text-xs">
                    Expired
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    <Clock className="mr-1 h-3 w-3" />
                    Pending
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => resendInvitation.mutate(invitation.id)}
                disabled={resendInvitation.isPending || deleteInvitation.isPending}
              >
                <RefreshCw className={`mr-1 h-3 w-3 ${isResending ? 'animate-spin' : ''}`} />
                Resend
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteInvitation.mutate(invitation.id)}
                disabled={resendInvitation.isPending || deleteInvitation.isPending}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inactive User Sheet
// ---------------------------------------------------------------------------

function InactiveUserSheet({
  user,
  canActivate,
  onClose,
}: {
  user: UserResponse;
  canActivate: boolean;
  onClose: () => void;
}): React.ReactElement {
  const reactivateUser = useReactivateUser();

  const handleReactivate = async () => {
    await reactivateUser.mutateAsync(user.id);
    toast.success(`${user.firstName} ${user.lastName} has been reactivated`);
    onClose();
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle>
          {user.firstName} {user.lastName}
        </SheetTitle>
        <SheetDescription>Inactive user</SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-4">
          <DetailSection title="Account Information">
            <DetailRow label="Email" value={user.email} />
          </DetailSection>

          <DetailSection title="Assigned Roles">
            {user.roles && user.roles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.roles.map((r) => (
                  <Badge key={r.id} variant={r.isSystem ? 'default' : 'secondary'}>
                    {r.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">No roles assigned</span>
            )}
          </DetailSection>
        </div>
      </SheetBody>

      {canActivate && (
        <SheetActions>
          <SheetCancel />
          <Button onClick={handleReactivate} disabled={reactivateUser.isPending}>
            <UserCheck className="mr-2 h-3.5 w-3.5" />
            {reactivateUser.isPending ? 'Reactivating...' : 'Reactivate'}
          </Button>
        </SheetActions>
      )}
    </>
  );
}
